import {
  getApiErrorMessage,
  getIntegrationDirigeraGetAllHubsQueryKey,
  getIntegrationDirigeraGetAllSensorMappingsQueryKey,
  getIntegrationDirigeraGetDiscoveryQueryKey,
  useIntegrationDirigeraAuthenticateHub,
  useIntegrationDirigeraCreateHub,
  useIntegrationDirigeraCreateSensorMapping,
  useIntegrationDirigeraDeleteHub,
  useIntegrationDirigeraDeleteSensorMapping,
  useIntegrationDirigeraDiscoverHub,
  useIntegrationDirigeraGetAllHubs,
  useIntegrationDirigeraGetAllSensorMappings,
  useIntegrationDirigeraGetDiscovery,
  useIntegrationDirigeraPauseHub,
  useIntegrationDirigeraResumeHub,
  useIntegrationDirigeraUpdateHub,
  useIntegrationDirigeraUpdateSensorMapping,
  type DirigeraDirigeraDiscoveryCandidate,
  type DirigeraDirigeraDiscoveryDevice,
  type DirigeraDirigeraHub,
  type DirigeraDirigeraSensorMapping,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { Button, SlideOutPanel, TextField } from '@/components/ui';

type Tab = 'hubs' | 'sensors';

type HubFormState = {
  name: string;
  host: string;
  port: string;
};

type SensorFormState = {
  humanId: string;
  name: string;
  transform: string;
};

type NoticeState = {
  tone: 'success' | 'error';
  message: string;
};

const emptyHubForm: HubFormState = {
  name: '',
  host: '',
  port: '8443',
};

const emptySensorForm: SensorFormState = {
  humanId: '',
  name: '',
  transform: '',
};

export function DirigeraSettingsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('hubs');
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const [hubPanelMode, setHubPanelMode] = useState<'create' | 'edit' | null>(null);
  const [hubFormError, setHubFormError] = useState<string | null>(null);
  const [editingHub, setEditingHub] = useState<DirigeraDirigeraHub | null>(null);

  const [sensorPanelMode, setSensorPanelMode] = useState<'create' | 'edit' | null>(null);
  const [sensorFormError, setSensorFormError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] =
    useState<DirigeraDirigeraDiscoveryCandidate | null>(null);
  const [editingMapping, setEditingMapping] = useState<DirigeraDirigeraSensorMapping | null>(null);

  const [pendingHubAction, setPendingHubAction] = useState<{ id: string; action: string } | null>(
    null
  );
  const [pendingMappingId, setPendingMappingId] = useState<string | null>(null);

  const hubsQuery = useIntegrationDirigeraGetAllHubs({
    query: {
      refetchInterval: 10000,
    },
  });

  const createHubMutation = useIntegrationDirigeraCreateHub();
  const updateHubMutation = useIntegrationDirigeraUpdateHub();
  const deleteHubMutation = useIntegrationDirigeraDeleteHub();
  const authenticateHubMutation = useIntegrationDirigeraAuthenticateHub();
  const discoverHubMutation = useIntegrationDirigeraDiscoverHub();
  const pauseHubMutation = useIntegrationDirigeraPauseHub();
  const resumeHubMutation = useIntegrationDirigeraResumeHub();

  const createSensorMutation = useIntegrationDirigeraCreateSensorMapping();
  const updateSensorMutation = useIntegrationDirigeraUpdateSensorMapping();
  const deleteSensorMutation = useIntegrationDirigeraDeleteSensorMapping();

  const hubs = useMemo(() => hubsQuery.data?.hubs ?? [], [hubsQuery.data?.hubs]);

  useEffect(() => {
    if (hubs.length === 0) {
      setSelectedHubId(null);
      return;
    }

    if (!selectedHubId || !hubs.some((hub) => hub.id === selectedHubId)) {
      setSelectedHubId(hubs[0]?.id ?? null);
    }
  }, [hubs, selectedHubId]);

  const selectedHub = useMemo(
    () => hubs.find((hub) => hub.id === selectedHubId) ?? null,
    [hubs, selectedHubId]
  );

  const discoveryQuery = useIntegrationDirigeraGetDiscovery(selectedHub?.id ?? '', {
    query: {
      enabled: !!selectedHub?.id,
      refetchInterval: selectedHub?.authenticated ? 10000 : false,
    },
  });

  const sensorMappingsQuery = useIntegrationDirigeraGetAllSensorMappings(
    selectedHub?.id ? { hubId: selectedHub.id } : undefined,
    {
      query: {
        enabled: !!selectedHub?.id,
        refetchInterval: selectedHub?.authenticated ? 10000 : false,
      },
    }
  );

  const sensorMappings = useMemo(
    () =>
      [...(sensorMappingsQuery.data?.sensors ?? [])].sort((left, right) =>
        (left.name ?? '').localeCompare(right.name ?? '')
      ),
    [sensorMappingsQuery.data?.sensors]
  );

  const mappingsById = useMemo(() => {
    return new Map(
      sensorMappings.filter((mapping) => mapping.id).map((mapping) => [mapping.id!, mapping])
    );
  }, [sensorMappings]);

  const discoveryDevices = useMemo(() => {
    return [...(discoveryQuery.data?.devices ?? [])].sort((left, right) =>
      (left.name ?? '').localeCompare(right.name ?? '')
    );
  }, [discoveryQuery.data?.devices]);

  const invalidateDirigeraData = async (hubId?: string) => {
    await queryClient.invalidateQueries({
      queryKey: getIntegrationDirigeraGetAllHubsQueryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: getIntegrationDirigeraGetAllSensorMappingsQueryKey(),
    });

    if (hubId) {
      await queryClient.invalidateQueries({
        queryKey: getIntegrationDirigeraGetDiscoveryQueryKey(hubId),
      });
      await queryClient.invalidateQueries({
        queryKey: getIntegrationDirigeraGetAllSensorMappingsQueryKey({ hubId }),
      });
    }
  };

  const openCreateHubPanel = () => {
    setEditingHub(null);
    setHubFormError(null);
    setHubPanelMode('create');
  };

  const openEditHubPanel = (hub: DirigeraDirigeraHub) => {
    setEditingHub(hub);
    setHubFormError(null);
    setHubPanelMode('edit');
  };

  const closeHubPanel = () => {
    setHubPanelMode(null);
    setEditingHub(null);
    setHubFormError(null);
  };

  const openCreateSensorPanel = (candidate: DirigeraDirigeraDiscoveryCandidate) => {
    setSelectedCandidate(candidate);
    setEditingMapping(null);
    setSensorFormError(null);
    setSensorPanelMode('create');
  };

  const openEditSensorPanel = (mapping: DirigeraDirigeraSensorMapping) => {
    setSelectedCandidate(null);
    setEditingMapping(mapping);
    setSensorFormError(null);
    setSensorPanelMode('edit');
  };

  const closeSensorPanel = () => {
    setSensorPanelMode(null);
    setSelectedCandidate(null);
    setEditingMapping(null);
    setSensorFormError(null);
  };

  const handleHubSubmit = async (hubForm: HubFormState) => {
    const name = hubForm.name.trim();
    const host = hubForm.host.trim();
    const port = Number.parseInt(hubForm.port.trim(), 10);

    setHubFormError(null);

    try {
      if (hubPanelMode === 'edit' && editingHub?.id) {
        const response = await updateHubMutation.mutateAsync({
          id: editingHub.id,
          data: { name, host, port },
        });
        await invalidateDirigeraData(editingHub.id);
        setNotice({
          tone: 'success',
          message: `Updated ${response.hub?.name ?? name}.`,
        });
      } else {
        const response = await createHubMutation.mutateAsync({
          data: { name, host, port },
        });
        const createdHubId = response.hub?.id;
        await invalidateDirigeraData(createdHubId);
        if (createdHubId) {
          setSelectedHubId(createdHubId);
        }
        setNotice({
          tone: 'success',
          message: `Added ${response.hub?.name ?? name}. Authenticate it to start discovery.`,
        });
      }
      closeHubPanel();
    } catch (error) {
      setHubFormError(getApiErrorMessage(error, 'Unable to save the Dirigera hub.'));
    }
  };

  const runHubAction = async (
    hub: DirigeraDirigeraHub,
    action: string,
    callback: () => Promise<void>
  ) => {
    if (!hub.id) {
      return;
    }

    setPendingHubAction({ id: hub.id, action });
    try {
      await callback();
    } finally {
      setPendingHubAction(null);
    }
  };

  const handleAuthenticateHub = async (hub: DirigeraDirigeraHub) => {
    await runHubAction(hub, 'authenticate', async () => {
      try {
        const response = await authenticateHubMutation.mutateAsync({ id: hub.id! });
        await invalidateDirigeraData(hub.id);
        setNotice({
          tone: 'success',
          message:
            response.status?.message ?? `Authenticated ${hub.name ?? 'Dirigera hub'} successfully.`,
        });
      } catch (error) {
        setNotice({
          tone: 'error',
          message: getApiErrorMessage(
            error,
            'Authentication failed. Press the action button on the hub and try again.'
          ),
        });
      }
    });
  };

  const handleDiscoverHub = async (hub: DirigeraDirigeraHub) => {
    await runHubAction(hub, 'discover', async () => {
      try {
        await discoverHubMutation.mutateAsync({ id: hub.id! });
        await invalidateDirigeraData(hub.id);
        setNotice({
          tone: 'success',
          message: `Refreshed discovery for ${hub.name ?? 'Dirigera hub'}.`,
        });
      } catch (error) {
        setNotice({
          tone: 'error',
          message: getApiErrorMessage(error, 'Unable to refresh Dirigera discovery.'),
        });
      }
    });
  };

  const handlePauseHub = async (hub: DirigeraDirigeraHub) => {
    await runHubAction(hub, 'pause', async () => {
      try {
        await pauseHubMutation.mutateAsync({ id: hub.id! });
        await invalidateDirigeraData(hub.id);
      } catch (error) {
        setNotice({
          tone: 'error',
          message: getApiErrorMessage(error, 'Unable to pause the Dirigera hub.'),
        });
      }
    });
  };

  const handleResumeHub = async (hub: DirigeraDirigeraHub) => {
    await runHubAction(hub, 'resume', async () => {
      try {
        await resumeHubMutation.mutateAsync({ id: hub.id! });
        await invalidateDirigeraData(hub.id);
      } catch (error) {
        setNotice({
          tone: 'error',
          message: getApiErrorMessage(error, 'Unable to resume the Dirigera hub.'),
        });
      }
    });
  };

  const handleDeleteHub = async (hub: DirigeraDirigeraHub) => {
    if (!hub.id) {
      return;
    }

    if (!window.confirm(`Delete ${hub.name ?? 'this Dirigera hub'} and all mapped sensors?`)) {
      return;
    }

    await runHubAction(hub, 'delete', async () => {
      try {
        await deleteHubMutation.mutateAsync({ id: hub.id! });
        await invalidateDirigeraData(hub.id);
        if (selectedHubId === hub.id) {
          const nextHub = hubs.find((item) => item.id !== hub.id);
          setSelectedHubId(nextHub?.id ?? null);
        }
        setNotice({
          tone: 'success',
          message: `${hub.name ?? 'Dirigera hub'} was removed.`,
        });
      } catch (error) {
        setNotice({
          tone: 'error',
          message: getApiErrorMessage(error, 'Unable to delete the Dirigera hub.'),
        });
      }
    });
  };

  const handleSensorSubmit = async (sensorForm: SensorFormState) => {
    const name = sensorForm.name.trim();
    const humanId = sensorForm.humanId.trim();
    const transform = sensorForm.transform.trim();

    if (sensorPanelMode === 'edit') {
      if (!editingMapping?.id) {
        setSensorFormError('Choose a mapped sensor first.');
        return;
      }
    } else if (!selectedHub?.id || !selectedCandidate) {
      setSensorFormError('Choose a discovered candidate first.');
      return;
    }

    setSensorFormError(null);

    try {
      if (sensorPanelMode === 'edit' && editingMapping?.id) {
        await updateSensorMutation.mutateAsync({
          id: editingMapping.id,
          data: {
            name,
            transform,
          },
        });
        await invalidateDirigeraData(editingMapping.hubId);
        setNotice({
          tone: 'success',
          message: `Updated ${name}.`,
        });
      } else if (selectedHub?.id && selectedCandidate) {
        await createSensorMutation.mutateAsync({
          data: {
            humanId,
            hubId: selectedHub.id,
            deviceId: selectedCandidate.deviceId ?? '',
            deviceType: selectedCandidate.deviceType ?? 'sensor',
            deviceName: selectedCandidate.deviceName ?? 'Dirigera device',
            attributePath: selectedCandidate.attributePath ?? '',
            attributeName: selectedCandidate.attributeName ?? 'Sensor value',
            valueType: selectedCandidate.valueType === 'boolean' ? 'boolean' : 'number',
            name,
            transform,
          },
        });
        await invalidateDirigeraData(selectedHub.id);
        setNotice({
          tone: 'success',
          message: `Configured ${name}. Sensor values will now flow into smarthome.`,
        });
      }
      closeSensorPanel();
    } catch (error) {
      setSensorFormError(getApiErrorMessage(error, 'Unable to save the Dirigera sensor mapping.'));
    }
  };

  const handleDeleteMapping = async (mapping: DirigeraDirigeraSensorMapping) => {
    if (!mapping.id) {
      return;
    }

    if (!window.confirm(`Delete ${mapping.name ?? 'this mapped sensor'}?`)) {
      return;
    }

    setPendingMappingId(mapping.id);
    try {
      await deleteSensorMutation.mutateAsync({ id: mapping.id });
      await invalidateDirigeraData(mapping.hubId);
      setNotice({
        tone: 'success',
        message: `${mapping.name ?? 'Mapped sensor'} was removed.`,
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        message: getApiErrorMessage(error, 'Unable to delete the Dirigera sensor mapping.'),
      });
    } finally {
      setPendingMappingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          IKEA Dirigera
        </h1>
        <p className="mt-1 max-w-3xl text-[15px] text-gray-500 dark:text-zinc-500">
          Add one or more Dirigera hubs, authenticate them with the physical action button, then
          discover any supported sensor attributes and map them into smarthome sensors.
        </p>
      </div>

      {notice ? <NoticeBanner notice={notice} onDismiss={() => setNotice(null)} /> : null}

      <div className="border-b border-gray-200 dark:border-white/[0.06]">
        <nav className="-mb-px flex gap-6">
          {(['hubs', 'sensors'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'hubs' && (
        <HubsTabContent
          hubs={hubs}
          isLoading={hubsQuery.isLoading}
          selectedHubId={selectedHubId}
          pendingHubAction={pendingHubAction}
          onSelectHub={(id) => setSelectedHubId(id)}
          onAddHub={openCreateHubPanel}
          onEditHub={openEditHubPanel}
          onAuthenticate={handleAuthenticateHub}
          onDiscover={handleDiscoverHub}
          onPause={handlePauseHub}
          onResume={handleResumeHub}
          onDelete={handleDeleteHub}
          onRefresh={() => void hubsQuery.refetch()}
        />
      )}

      {activeTab === 'sensors' && (
        <SensorsTabContent
          hubs={hubs}
          selectedHubId={selectedHubId}
          selectedHub={selectedHub}
          discoveryDevices={discoveryDevices}
          mappingsById={mappingsById}
          isLoadingDiscovery={discoveryQuery.isLoading}
          pendingMappingId={pendingMappingId}
          onSelectHub={(id) => setSelectedHubId(id)}
          onConfigureSensor={openCreateSensorPanel}
          onEditSensor={openEditSensorPanel}
          onDeleteMapping={handleDeleteMapping}
        />
      )}

      <DirigeraHubPanel
        isOpen={hubPanelMode !== null}
        mode={hubPanelMode ?? 'create'}
        hub={editingHub}
        onClose={closeHubPanel}
        onSubmit={handleHubSubmit}
        isLoading={createHubMutation.isPending || updateHubMutation.isPending}
        error={hubFormError}
      />

      <DirigeraSensorPanel
        isOpen={sensorPanelMode !== null}
        mode={sensorPanelMode ?? 'create'}
        candidate={selectedCandidate}
        mapping={editingMapping}
        onClose={closeSensorPanel}
        onSubmit={handleSensorSubmit}
        isLoading={createSensorMutation.isPending || updateSensorMutation.isPending}
        error={sensorFormError}
      />
    </div>
  );
}

// ============================================================================
// HUBS TAB
// ============================================================================

interface HubsTabContentProps {
  hubs: DirigeraDirigeraHub[];
  isLoading: boolean;
  selectedHubId: string | null;
  pendingHubAction: { id: string; action: string } | null;
  onSelectHub: (id: string | null) => void;
  onAddHub: () => void;
  onEditHub: (hub: DirigeraDirigeraHub) => void;
  onAuthenticate: (hub: DirigeraDirigeraHub) => Promise<void>;
  onDiscover: (hub: DirigeraDirigeraHub) => Promise<void>;
  onPause: (hub: DirigeraDirigeraHub) => Promise<void>;
  onResume: (hub: DirigeraDirigeraHub) => Promise<void>;
  onDelete: (hub: DirigeraDirigeraHub) => Promise<void>;
  onRefresh: () => void;
}

function HubsTabContent({
  hubs,
  isLoading,
  selectedHubId,
  pendingHubAction,
  onSelectHub,
  onAddHub,
  onEditHub,
  onAuthenticate,
  onDiscover,
  onPause,
  onResume,
  onDelete,
  onRefresh,
}: HubsTabContentProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Hubs
          </h2>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={onAddHub}>
          Add hub
        </Button>
      </div>

      {isLoading ? (
        <PanelMessage message="Loading Dirigera hubs..." />
      ) : hubs.length === 0 ? (
        <PanelMessage message="No Dirigera hubs configured yet." />
      ) : (
        <div className="space-y-4">
          {hubs.map((hub) => {
            const isSelected = selectedHubId === hub.id;
            const connectionTone = connectionToneClass(hub.connection?.state);

            return (
              <button
                key={hub.id ?? hub.name}
                type="button"
                onClick={() => onSelectHub(hub.id ?? null)}
                className={`w-full rounded-2xl border p-5 text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50/70 dark:border-blue-400 dark:bg-blue-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.06] dark:bg-[#18181b] dark:hover:border-white/[0.12]'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {hub.name}
                    </div>
                    <div className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
                      {hub.host}:{hub.port ?? 8443}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${connectionTone}`}
                    >
                      {formatConnectionLabel(hub.connection?.state)}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        hub.authenticated
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                      }`}
                    >
                      {hub.authenticated ? 'Authenticated' : 'Needs auth'}
                    </span>
                  </div>
                </div>

                <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                  <MetadataItem label="Last sync" value={formatDateTime(hub.lastSyncedAt)} />
                  <MetadataItem
                    label="Last error"
                    value={hub.connection?.lastError?.trim() || 'None'}
                    error={!!hub.connection?.lastError}
                  />
                </dl>

                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    loading={
                      pendingHubAction?.id === hub.id && pendingHubAction?.action === 'authenticate'
                    }
                    leftIcon={<ShieldCheck className="h-4 w-4" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      void onAuthenticate(hub);
                    }}
                  >
                    Authenticate
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={
                      pendingHubAction?.id === hub.id && pendingHubAction?.action === 'discover'
                    }
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      void onDiscover(hub);
                    }}
                  >
                    Discover
                  </Button>
                  {hub.connection?.state === 'paused' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        pendingHubAction?.id === hub.id && pendingHubAction?.action === 'resume'
                      }
                      leftIcon={<Play className="h-4 w-4" />}
                      onClick={(event) => {
                        event.stopPropagation();
                        void onResume(hub);
                      }}
                    >
                      Resume
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        pendingHubAction?.id === hub.id && pendingHubAction?.action === 'pause'
                      }
                      leftIcon={<Pause className="h-4 w-4" />}
                      onClick={(event) => {
                        event.stopPropagation();
                        void onPause(hub);
                      }}
                    >
                      Pause
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Pencil className="h-4 w-4" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditHub(hub);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={
                      pendingHubAction?.id === hub.id && pendingHubAction?.action === 'delete'
                    }
                    leftIcon={<Trash2 className="h-4 w-4" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      void onDelete(hub);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SENSORS TAB
// ============================================================================

interface SensorsTabContentProps {
  hubs: DirigeraDirigeraHub[];
  selectedHubId: string | null;
  selectedHub: DirigeraDirigeraHub | null;
  discoveryDevices: DirigeraDirigeraDiscoveryDevice[];
  mappingsById: Map<string, DirigeraDirigeraSensorMapping>;
  isLoadingDiscovery: boolean;
  pendingMappingId: string | null;
  onSelectHub: (id: string | null) => void;
  onConfigureSensor: (candidate: DirigeraDirigeraDiscoveryCandidate) => void;
  onEditSensor: (mapping: DirigeraDirigeraSensorMapping) => void;
  onDeleteMapping: (mapping: DirigeraDirigeraSensorMapping) => Promise<void>;
}

function SensorsTabContent({
  hubs,
  selectedHubId,
  selectedHub,
  discoveryDevices,
  mappingsById,
  isLoadingDiscovery,
  pendingMappingId,
  onSelectHub,
  onConfigureSensor,
  onEditSensor,
  onDeleteMapping,
}: SensorsTabContentProps) {
  const flatCandidates = useMemo(
    () =>
      discoveryDevices
        .flatMap((device) => (device.candidates ?? []).map((candidate) => ({ candidate, device })))
        .sort(
          (a, b) =>
            (a.candidate.deviceName ?? '').localeCompare(b.candidate.deviceName ?? '') ||
            (a.candidate.attributeName ?? '').localeCompare(b.candidate.attributeName ?? '')
        ),
    [discoveryDevices]
  );

  return (
    <div className="space-y-6">
      {hubs.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {hubs.map((hub) => (
            <button
              key={hub.id}
              type="button"
              onClick={() => onSelectHub(hub.id ?? null)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedHubId === hub.id
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-zinc-400 dark:hover:bg-white/[0.10]'
              }`}
            >
              {hub.name}
            </button>
          ))}
        </div>
      )}

      {!selectedHub ? (
        <PanelMessage message="Select a hub to view its sensors." />
      ) : !selectedHub.authenticated ? (
        <PanelMessage message="Authenticate the hub first — press Authenticate, then the action button on the physical hub within 60 seconds." />
      ) : isLoadingDiscovery ? (
        <PanelMessage message="Loading sensors..." />
      ) : flatCandidates.length === 0 ? (
        <PanelMessage message="No sensor-capable attributes found yet. Trigger discovery after the hub connects." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
          {flatCandidates.map(({ candidate, device }, index) => {
            const mapping = candidate.mappedSensorId
              ? mappingsById.get(candidate.mappedSensorId)
              : undefined;
            return (
              <DiscoveryCandidateRow
                key={candidate.candidateId ?? `${candidate.deviceId}-${candidate.attributePath}`}
                candidate={candidate}
                device={device}
                mapping={mapping}
                isFirst={index === 0}
                deletingMappingId={pendingMappingId}
                onConfigure={onConfigureSensor}
                onEdit={() => mapping && onEditSensor(mapping)}
                onDelete={() => mapping && void onDeleteMapping(mapping)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function DiscoveryCandidateRow({
  candidate,
  device,
  mapping,
  isFirst,
  deletingMappingId,
  onConfigure,
  onEdit,
  onDelete,
}: {
  candidate: DirigeraDirigeraDiscoveryCandidate;
  device: DirigeraDirigeraDiscoveryDevice;
  mapping: DirigeraDirigeraSensorMapping | undefined;
  isFirst: boolean;
  deletingMappingId: string | null;
  onConfigure: (candidate: DirigeraDirigeraDiscoveryCandidate) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const currentValue = formatCandidateValue(
    candidate.currentNumberValue,
    candidate.currentBooleanValue
  );

  return (
    <div
      className={`flex items-center justify-between p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
          <Activity className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
        </div>
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">
            {candidate.attributeName}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-gray-500 dark:text-zinc-500">
            <span>{device.name}</span>
            {device.roomName ? (
              <>
                <span className="text-gray-300 dark:text-zinc-700">·</span>
                <span>{device.roomName}</span>
              </>
            ) : null}
            {currentValue !== '—' ? (
              <>
                <span className="text-gray-300 dark:text-zinc-700">·</span>
                <span className="font-mono text-xs">{currentValue}</span>
              </>
            ) : null}
            {mapping ? (
              <>
                <span className="text-gray-300 dark:text-zinc-700">·</span>
                <span>{mapping.name}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="ml-4 flex flex-shrink-0 items-center gap-2">
        {mapping ? (
          <>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={deletingMappingId === mapping.id}
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => onConfigure(candidate)}
          >
            Configure
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

function NoticeBanner({ notice, onDismiss }: { notice: NoticeState; onDismiss: () => void }) {
  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 ${
        notice.tone === 'success'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300'
          : 'border-red-200 bg-red-50 text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300'
      }`}
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="text-sm">{notice.message}</div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-sm font-medium opacity-70 transition-opacity hover:opacity-100"
      >
        Dismiss
      </button>
    </div>
  );
}

function MetadataItem({
  label,
  value,
  error = false,
}: {
  label: string;
  value: string;
  error?: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-white/[0.06] dark:bg-white/[0.03]">
      <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-500">{label}</dt>
      <dd
        className={`mt-1 text-sm ${
          error ? 'text-red-600 dark:text-red-300' : 'text-gray-900 dark:text-white'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function PanelMessage({ message, compact = false }: { message: string; compact?: boolean }) {
  return (
    <div
      className={`rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 text-center text-sm text-gray-500 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-zinc-500 ${
        compact ? 'px-4 py-6' : 'px-6 py-10'
      }`}
    >
      {message}
    </div>
  );
}

// ============================================================================
// SLIDE-OUT PANELS
// ============================================================================

interface DirigeraHubPanelProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  hub: DirigeraDirigeraHub | null;
  onClose: () => void;
  onSubmit: (data: HubFormState) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function DirigeraHubPanel({
  isOpen,
  mode,
  hub,
  onClose,
  onSubmit,
  isLoading,
  error,
}: DirigeraHubPanelProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HubFormState>({
    defaultValues: getHubFormValues(hub),
  });

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset(getHubFormValues(hub));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, hub, reset]);

  const handleClose = () => {
    reset(getHubFormValues(hub));
    onClose();
  };

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="dirigera-hub-form" loading={isLoading}>
        {mode === 'edit' ? 'Save hub' : 'Add hub'}
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'edit' ? 'Edit hub' : 'Add hub'}
      subtitle="Use the hub IP or hostname on your local network. Dirigera uses port 8443 by default."
      footer={footer}
    >
      <form id="dirigera-hub-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living room Dirigera"
          error={errors.name?.message}
          {...register('name', {
            validate: (value) => (value.trim() ? true : 'Enter a hub name.'),
          })}
        />
        <TextField
          label="Host or IP"
          placeholder="192.168.1.50"
          error={errors.host?.message}
          {...register('host', {
            validate: (value) => (value.trim() ? true : 'Enter a host or IP address.'),
          })}
        />
        <TextField
          label="Port"
          inputMode="numeric"
          placeholder="8443"
          error={errors.port?.message}
          {...register('port', {
            validate: (value) => {
              const port = Number.parseInt(value.trim(), 10);
              return !Number.isNaN(port) && port > 0 ? true : 'Enter a valid port.';
            },
          })}
        />
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        ) : null}
      </form>
    </SlideOutPanel>
  );
}

interface DirigeraSensorPanelProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  candidate: DirigeraDirigeraDiscoveryCandidate | null;
  mapping: DirigeraDirigeraSensorMapping | null;
  onClose: () => void;
  onSubmit: (data: SensorFormState) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function DirigeraSensorPanel({
  isOpen,
  mode,
  candidate,
  mapping,
  onClose,
  onSubmit,
  isLoading,
  error,
}: DirigeraSensorPanelProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SensorFormState>({
    defaultValues: getSensorFormValues(mode, candidate, mapping),
  });

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset(getSensorFormValues(mode, candidate, mapping));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, candidate, mapping, mode, reset]);

  const handleClose = () => {
    reset(getSensorFormValues(mode, candidate, mapping));
    onClose();
  };

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="dirigera-sensor-form" loading={isLoading}>
        {mode === 'edit' ? 'Save sensor' : 'Create sensor'}
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'edit' ? 'Edit mapped sensor' : 'Configure sensor'}
      subtitle={
        mode === 'edit'
          ? 'Update the mapped sensor name or transform.'
          : 'Create a smarthome sensor from this discovered Dirigera attribute.'
      }
      footer={footer}
    >
      <form id="dirigera-sensor-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {mode === 'create' && candidate ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-white/[0.06] dark:bg-white/[0.03]">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {candidate.deviceName}
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
              {candidate.attributeName} · {candidate.attributePath}
            </div>
            <div className="mt-2 text-sm text-gray-600 dark:text-zinc-400">
              Current value:{' '}
              {formatCandidateValue(candidate.currentNumberValue, candidate.currentBooleanValue)}
            </div>
          </div>
        ) : null}

        {mode === 'create' ? (
          <TextField
            label="Human ID"
            placeholder="dirigera.air-sensor.currenttemperature"
            error={errors.humanId?.message}
            {...register('humanId', {
              validate: (value) => (value.trim() ? true : 'Human ID is required.'),
            })}
          />
        ) : (
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-white/[0.06] dark:bg-white/[0.03]">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-500">
              Human ID
            </div>
            <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
              {mapping?.humanId}
            </div>
          </div>
        )}

        <TextField
          label="Sensor name"
          placeholder="Living room air temperature"
          error={errors.name?.message}
          {...register('name', {
            validate: (value) => (value.trim() ? true : 'Sensor name is required.'),
          })}
        />
        <TextField
          label="Transform"
          placeholder="value"
          hint="Optional govaluate expression. Example: value / 100 or value * 1.8 + 32"
          {...register('transform')}
        />

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        ) : null}
      </form>
    </SlideOutPanel>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatConnectionLabel(state?: string) {
  switch (state) {
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting';
    case 'paused':
      return 'Paused';
    case 'auth-required':
      return 'Auth required';
    case 'not-connected':
      return 'Disconnected';
    default:
      return 'Unknown';
  }
}

function connectionToneClass(state?: string) {
  switch (state) {
    case 'connected':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'connecting':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300';
    case 'paused':
      return 'bg-gray-100 text-gray-700 dark:bg-white/[0.06] dark:text-zinc-300';
    case 'auth-required':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    default:
      return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
  }
}

function formatDateTime(value?: string) {
  if (!value) {
    return 'Never';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function formatCandidateValue(numberValue?: number, booleanValue?: boolean) {
  if (typeof numberValue === 'number') {
    return Number.isInteger(numberValue) ? `${numberValue}` : numberValue.toFixed(2);
  }

  if (typeof booleanValue === 'boolean') {
    return booleanValue ? 'true' : 'false';
  }

  return '—';
}

function getHubFormValues(hub: DirigeraDirigeraHub | null): HubFormState {
  if (!hub) {
    return emptyHubForm;
  }

  return {
    name: hub.name ?? '',
    host: hub.host ?? '',
    port: hub.port ? `${hub.port}` : '8443',
  };
}

function getSensorFormValues(
  mode: 'create' | 'edit',
  candidate: DirigeraDirigeraDiscoveryCandidate | null,
  mapping: DirigeraDirigeraSensorMapping | null
): SensorFormState {
  if (mode === 'create') {
    return {
      humanId: candidate?.suggestedHumanId ?? '',
      name: candidate?.suggestedName ?? '',
      transform: '',
    };
  }

  if (!mapping) {
    return emptySensorForm;
  }

  return {
    humanId: mapping.humanId ?? '',
    name: mapping.name ?? '',
    transform: mapping.transform ?? '',
  };
}
